import {FormInstance, Select} from 'antd';
import {useEffect, useMemo, useRef, useState} from 'react';

import {Locale} from '../../../../../provider/locale/locale';
import {useAspects} from '../../../../../service/reivew-analysis/review-aspect/review-aspect-hook';
import {debounce} from '../../../../../util/function';
import {useReviewsAnalysisFilter} from '../../reviews-analysis-hook';
import {ReviewsAnalysisFilterEnum, ReviewsAnalysisFilterType} from '../../reviews-analysis-type';
import {renderSelectOption} from '../drawer-filter/drawer-filter-helper';
import {SelectOptionType} from '../drawer-filter/drawer-filter-type';

type PropsType = {
    value?: string;
    onChange?: (value: string) => void;
    form: FormInstance<ReviewsAnalysisFilterType>;
};

export function AspectSelect(props: PropsType): JSX.Element {
    const {value, onChange, form} = props;
    const isFirstRender = useRef(true);

    const [q, setQ] = useState<string>('');

    const singleFilter = useReviewsAnalysisFilter();

    const {data: aspectsResult, isLoading} = useAspects(q);

    const {data: aspectLabels, isLoading: isAspectsLoading} = useAspects(
        '',
        singleFilter.filter[ReviewsAnalysisFilterEnum.AspectIds]
    );

    const aspectsOptions: Array<SelectOptionType<string>> = useMemo((): Array<SelectOptionType<string>> => {
        return (
            aspectsResult?.map<SelectOptionType<string>>(
                (aspect): SelectOptionType<string> => ({
                    id: aspect.ids.join(','),
                    name: aspect.title,
                })
            ) || []
        );
    }, [aspectsResult]);

    const aspectLabelsOptions: Array<SelectOptionType<string>> = useMemo((): Array<SelectOptionType<string>> => {
        return (
            aspectLabels?.map<SelectOptionType<string>>(
                (aspect): SelectOptionType<string> => ({
                    id: aspect.ids.join(','),
                    name: aspect.title,
                })
            ) || []
        );
    }, [aspectLabels]);

    const uniqueAspectOptions: Array<SelectOptionType<string>> = useMemo(() => {
        const merged = [...aspectsOptions, ...aspectLabelsOptions];
        const uniqueMap = new Map<string, SelectOptionType<string>>();

        merged.forEach((item) => {
            if (!uniqueMap.has(item.id)) {
                uniqueMap.set(item.id, item);
            }
        });

        return [...uniqueMap.values()];
    }, [aspectsOptions, aspectLabelsOptions]);

    useEffect(() => {
        if (
            isFirstRender.current &&
            uniqueAspectOptions.length > 0 &&
            singleFilter.filter[ReviewsAnalysisFilterEnum.AspectIds].length > 0
        ) {
            const allValues = uniqueAspectOptions.map((option) => option.id);

            form.setFieldValue([ReviewsAnalysisFilterEnum.AspectIds], allValues);
            isFirstRender.current = false;
        }
    }, [uniqueAspectOptions]);

    return (
        <Select<string>
            disabled={false}
            loading={isLoading || isAspectsLoading}
            mode="multiple"
            // eslint-disable-next-line no-undefined
            notFoundContent={q === '' ? null : undefined}
            onChange={onChange}
            onSearch={debounce<[string]>(setQ, 300)}
            optionFilterProp="children"
            placeholder={<Locale stringKey="REVIEWS_ANALYSIS__FORM__INPUT__ASPECTS__PLACEHOLDER" />}
            value={value}
        >
            {uniqueAspectOptions.map(renderSelectOption)}
        </Select>
    );
}
